import { LitElement, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { Constructable } from './types';

export interface SDSPopoverTargetMixinInterface {
	popoverTarget?: string;
	popoverTargetAction?: string;
}

export function SDSPopoverTargetMixin<T extends Constructable<LitElement>>(superClass: T): Constructable<SDSPopoverTargetMixinInterface> & T {
	class SDSPopoverTarget extends superClass {
		@property({ type: String, attribute: 'popovertarget' }) popoverTarget?: string;
		@property({ type: String, attribute: 'popovertargetaction' }) popoverTargetAction?: string;

		private _popoverElement?: Element;
		private _popoverToggleHandler?: EventListener;

		override updated(changed: PropertyValues) {
			super.updated(changed);

			if (changed.has('popoverTarget') || changed.has('popoverTargetAction')) {
				this._updatePopoverTarget();
			}
		}

		override disconnectedCallback() {
			super.disconnectedCallback();
			this._cleanupPopoverListener();
		}

		private _cleanupPopoverListener() {
			if (this._popoverToggleHandler && this._popoverElement) {
				this._popoverElement.removeEventListener('toggle', this._popoverToggleHandler);
				this._popoverToggleHandler = undefined;
				this._popoverElement = undefined;
			}
		}

		private _updatePopoverTarget() {
			const button = this.shadowRoot?.querySelector<HTMLButtonElement>('#control');
			if (!button) {
				return;
			}

			this._cleanupPopoverListener();

			if (this.popoverTarget) {
				const root = this.getRootNode() as Document | ShadowRoot;
				const targetElement = root.getElementById?.(this.popoverTarget) ?? document.getElementById(this.popoverTarget);

				if (targetElement) {
					button.popoverTargetElement = targetElement;
					this._popoverElement = targetElement;

					const targetRole = targetElement.getAttribute('role');

					if (targetRole === 'tooltip') {
						button.setAttribute('aria-describedby', this.popoverTarget);
					} else {
						// Auto-toggle aria-expanded when the popover opens/closes
						this._popoverToggleHandler = (e: Event) => {
							const expanded = (e as ToggleEvent).newState === 'open';
							button.setAttribute('aria-expanded', String(expanded));
						};
						targetElement.addEventListener('toggle', this._popoverToggleHandler);

						button.setAttribute('aria-haspopup', targetRole || 'dialog');
						button.setAttribute('aria-expanded', 'false');
						button.setAttribute('aria-controls', this.popoverTarget);
					}
				}

				if (this.popoverTargetAction) {
					button.popoverTargetAction = this.popoverTargetAction;
				}
			} else {
				button.popoverTargetElement = null;
				button.popoverTargetAction = '';
				button.removeAttribute('aria-haspopup');
				button.removeAttribute('aria-expanded');
				button.removeAttribute('aria-controls');
				button.removeAttribute('aria-describedby');
			}
		}
	}

	return SDSPopoverTarget;
}
